import { useEffect, useState } from 'react';
import useAuth from '@/hooks/useAuth';
import { authoriseMultiSignAction, rejectMultiSignAction } from '@/services/multiSign';
import {
  getScheduledPaymentGroupStatus,
  markScheduledPaymentGroupAsComplete
} from '@/services/batchPayment';
import {
  MultiSignActionAuthorisationStateEnum,
  MultiSignActionStateEnum,
  MultiSignErrorCodeEnum,
  TMultiSignAction,
  TMultiSignActionState
} from '@/services/types/MultiSign';
import { ErrorResponseBody, unwrapPromiseError } from '@/services/types/ResponseBody';
import {
  ScheduledBankPaymentGroupStateEnum,
  ScheduledPaymentIntervalTypeEnum,
  TScheduledBankPaymentGroupState
} from '@/features/payments/types/BatchPayment';
import {
  batchOverviewModal,
  scheduledPaymentModal,
  scheduledTransferModal,
  rejectPaymentModal
} from '@/utils/translations';
import { PaymentErrorCodeEnum } from '@/features/payments/utils/errorCodes';
import { PaymentActionVariantEnum } from '../data/paymentActions';
import { getPaymentActionStrategy } from '../data/paymentActionStrategies';
import { isBatchAllFailed, isPaymentCreator } from '../data/paymentStatus';
import { PaymentRowKindEnum, TPaymentListItem, TPaymentRowKind } from '../types/ManagePayments';
import { resolvePaymentActionVariant } from '../utils/resolvePaymentActionVariant';
import { isWithinDeleteBlockWindow } from '../utils/isWithinDeleteBlockWindow';
import { TPaymentUpdate } from '../utils/applyPaymentUpdateToGroups';
import { dispatchApprovalRequiredResolved } from '../utils/approvalRequiredEvents';
import {
  AlreadyActionedOutcomeEnum,
  TAlreadyActionedOutcome
} from '../data/alreadyActionedOutcome';
import { UnableToDeleteReasonEnum, TUnableToDeleteReason } from '../data/unableToDeleteReason';
import { patchCachedAuthorisation } from './useMultiSignApprovers';

/**
 * Maps the multi-sign action's own state to the group/payment state a caller can pass to
 * onPaymentUpdate - null for PENDING, since that means only this customer's own authorisation was
 * already resolved (elsewhere) while the action as a whole is still awaiting other approvers, so
 * the payment's own `state` field (still PENDING_APPROVAL) doesn't need to change at all.
 */
const mapMultiSignStateToGroupState = (
  state: TMultiSignActionState
): TScheduledBankPaymentGroupState | null => {
  switch (state) {
    case MultiSignActionStateEnum.COMPLETED:
      return ScheduledBankPaymentGroupStateEnum.APPROVED;
    case MultiSignActionStateEnum.REJECTED:
      return ScheduledBankPaymentGroupStateEnum.REJECTED;
    case MultiSignActionStateEnum.EXPIRED:
      return ScheduledBankPaymentGroupStateEnum.EXPIRED;
    case MultiSignActionStateEnum.PENDING:
    default:
      return null;
  }
};

/**
 * Either "not pending" error code (see MultiSignErrorCodeEnum) means the same thing to this hook
 * - this request arrived too late, so refetch and reconcile instead of just showing a dead-end
 * error. AUTHORISATION_NOT_PENDING (this customer's own slot already resolved) and
 * ACTION_NOT_PENDING (the action as a whole already reached a terminal outcome) only differ in
 * backend wording, not in what the frontend needs to do about them.
 */
const isAuthorisationConflictError = (code: string | undefined): boolean =>
  code === MultiSignErrorCodeEnum.AUTHORISATION_NOT_PENDING ||
  code === MultiSignErrorCodeEnum.ACTION_NOT_PENDING;

/**
 * Only ever thrown from authoriseMultiSignAction itself for an individual scheduled payment (see
 * MultiSignErrorCodeEnum.EXECUTION_FAILED) - the final required approval also runs that day's
 * payment synchronously, and this is that run failing, not the approval. Generic - the backend
 * collapses every execution-time failure (insufficient balance or otherwise) into this same code,
 * so it can't be trusted to mean specifically insufficient balance, only "the payment run itself
 * failed" - see MultiSignErrorCodeEnum's own doc comment. A batch's equivalent run is
 * asynchronous, so approve() never sees this for one; see its post-approval group-status fetch
 * instead.
 */
const isExecutionFailedError = (code: string | undefined): boolean =>
  code === MultiSignErrorCodeEnum.EXECUTION_FAILED;

/**
 * Retrying an individual scheduled payment or scheduled transfer runs it synchronously, same
 * shape as isExecutionFailedError above, but reached via an explicit retry rather than the final
 * required approval. Unlike EXECUTION_FAILED, INSUFFICIENT_FUNDS (4017) actually confirms the
 * cause, so retry() below can show the specific insufficient-funds outcome screen instead of
 * falling back to the generic retry-error banner. A batch's retry endpoint never throws this -
 * see retryPayment's own doc comment in paymentActionStrategyExtensions.ts - so this only ever
 * fires for scheduled_payment/scheduled_transfer in practice.
 */
const isInsufficientFundsError = (code: string | undefined): boolean =>
  code === PaymentErrorCodeEnum.INSUFFICIENT_FUNDS;

/**
 * Which generic banner retry()'s catch block falls back to for a non-insufficient-funds failure -
 * each kind's own modal names the thing being retried differently (payment/transfer/payments), so
 * this can't just be one shared string like it used to be.
 */
const RETRY_ERROR_BANNER_BY_KIND: Record<TPaymentRowKind, string> = {
  [PaymentRowKindEnum.BATCH]: batchOverviewModal.retryErrorBanner,
  [PaymentRowKindEnum.SCHEDULED_PAYMENT]: scheduledPaymentModal.retryErrorBanner,
  [PaymentRowKindEnum.PENDING_SCHEDULED_PAYMENT]: scheduledPaymentModal.retryErrorBanner,
  [PaymentRowKindEnum.SCHEDULED_TRANSFER]: scheduledTransferModal.retryErrorBanner
};

/**
 * Derives which AlreadyActionedModal copy to show from the refetched action - distinguishing "you
 * did this (from another device)" from "someone else did" wherever the wording actually differs,
 * by checking this customer's own authorisation slot on the fresh action.
 */
const resolveAlreadyActionedOutcome = (
  action: TMultiSignAction,
  currentCustomerId?: string
): TAlreadyActionedOutcome => {
  const ownAuthorisation = action.authorisations.find(
    (authorisation) => authorisation.customerId === currentCustomerId
  );

  switch (action.state) {
    case MultiSignActionStateEnum.COMPLETED:
      return ownAuthorisation?.state === MultiSignActionAuthorisationStateEnum.APPROVED
        ? AlreadyActionedOutcomeEnum.APPROVED_BY_YOU
        : AlreadyActionedOutcomeEnum.APPROVED;
    case MultiSignActionStateEnum.REJECTED:
      return ownAuthorisation?.state === MultiSignActionAuthorisationStateEnum.REJECTED
        ? AlreadyActionedOutcomeEnum.REJECTED_BY_YOU
        : AlreadyActionedOutcomeEnum.REJECTED;
    case MultiSignActionStateEnum.EXPIRED:
      return AlreadyActionedOutcomeEnum.EXPIRED;
    case MultiSignActionStateEnum.PENDING:
    default:
      // Only reachable when this customer's own authorisation was already resolved (elsewhere)
      // while the rule still needs other approvers - a single rejection always fully resolves
      // the whole action (see confirmReject), so a still-PENDING action here can only mean an
      // approval, never a rejection.
      return AlreadyActionedOutcomeEnum.APPROVED_PENDING_BY_YOU;
  }
};

export type { TPaymentUpdate };

export interface TScheduledPaymentActionCallbacks {
  /**
   * Called with the row's known-new state after a successful approve/reject so the caller (the
   * payments table) can move it into the right tab without refetching the whole list.
   */
  onPaymentUpdate?: (update: TPaymentUpdate) => void;
  /**
   * Called with the row's id once a delete/removal is confirmed. Both `scheduled_payment` and
   * `batch` rows now call a real delete endpoint - see `confirmDelete` below. Also used to drop a
   * `pending_scheduled_payment` row once its multi-sign action is fully approved or rejected,
   * since its id is the multi-sign action's id, not a real payment id - see the comment in
   * `approve`/`confirmReject`.
   */
  onPaymentDelete?: (id: string) => void;
  /**
   * True when the authoritative authorisation list (see useMultiSignApprovers) shows THIS viewer
   * specifically already approved this action in a past session - the caller is expected to scope
   * this to `payment.state === PENDING_APPROVAL` itself, since once the action is decided one way
   * or the other this stops being relevant. Folded into the same AWAITING_ADDITIONAL_APPROVAL
   * variant as `hasApprovedLocally` below, so a reopened modal hides Approve/Reject exactly like
   * one left open right after approving.
   *
   * Deliberately NOT keyed off "has anyone at all approved" - a co-approver having already
   * approved must not hide this viewer's own Approve/Reject if they themselves haven't acted yet.
   * That wider "anyone approved" concept only matters for the status label/banner text (see
   * useApprovalDisplayState's `isAwaitingAdditionalApproval`), a display concern this hook - which
   * only decides which controls render - has no reason to know about.
   */
  hasPersistedApproval?: boolean;
  /**
   * True when the authoritative authorisation list (see useMultiSignApprovers) shows the current
   * user has no authorisation slot on this action at all - they were never one of the required
   * signatories, so Approve/Reject should never render for them regardless of how many other
   * approvers are still pending. Same PENDING_APPROVAL-only scoping as hasPersistedApproval.
   */
  hasNoApprovalAuthority?: boolean;
  /**
   * Forces a live re-fetch of the multi-sign action (see useMultiSignApprovers.refetch), used to
   * recover from a 4100 "authorisation not pending" response - someone (possibly this same
   * customer, from another device) already approved/rejected it since this screen last checked.
   * Optional only for callers that never render an APPROVAL_REQUIRED variant at all (this hook
   * also drives delete/retry/mark-as-complete, none of which can hit this error).
   */
  refetchApprovers?: () => Promise<TMultiSignAction | null>;
}

/**
 * Drives the modal's action area. Which controls to show is decided by ACTION_BY_STATE (see
 * data/paymentActions.ts) - this hook only owns the interaction state layered on top of it,
 * plus the authorise/reject network calls.
 */
export const useScheduledPaymentAction = (
  payment: TPaymentListItem | null,
  {
    onPaymentUpdate,
    onPaymentDelete,
    hasPersistedApproval = false,
    hasNoApprovalAuthority = false,
    refetchApprovers
  }: TScheduledPaymentActionCallbacks = {}
) => {
  const auth = useAuth();

  // Set once a stale approve/reject call is reconciled (see reconcileAfterAuthorisationConflict) -
  // drives AlreadyActionedModal, which explains what actually happened before the caller closes
  // back to the payments list. Distinct from approveError/rejectError (the plain-text banner used
  // when reconciliation itself isn't possible, e.g. refetchApprovers wasn't supplied or its own
  // fetch failed).
  const [authorisationConflictOutcome, setAuthorisationConflictOutcome] =
    useState<TAlreadyActionedOutcome | null>(null);
  const dismissAuthorisationConflict = () => setAuthorisationConflictOutcome(null);

  /**
   * Shared by approve()/confirmReject()'s catch blocks: refetches the action fresh, patches the
   * payment's own `state` if it reached a terminal outcome (COMPLETED/REJECTED/EXPIRED), and sets
   * authorisationConflictOutcome so AlreadyActionedModal can explain what happened. Deliberately
   * does NOT set hasFinalApproval/hasRejected (the flags that drive the congratulatory full-screen
   * result) - this session didn't necessarily just do that action itself. Returns whether a
   * conflict was actually reconciled, so the caller knows whether to fall back to its own generic
   * error message instead.
   */
  const reconcileAfterAuthorisationConflict = async (
    activePayment: TPaymentListItem
  ): Promise<boolean> => {
    if (!refetchApprovers) return false;

    const freshAction = await refetchApprovers();
    if (!freshAction) return false;

    const newGroupState = mapMultiSignStateToGroupState(freshAction.state);
    if (newGroupState) {
      onPaymentUpdate?.({ id: activePayment.id, state: newGroupState });
    }
    // Same reasoning as the terminal-outcome branches in approve()/confirmReject() above - once
    // the refetched action is no longer PENDING it has reached a terminal outcome (COMPLETED,
    // REJECTED or EXPIRED, whether by this customer or someone else), so it has definitely left
    // the pending-approval count and the nav badge can decrement locally. A still-PENDING action
    // means only this customer's own slot was resolved elsewhere while other approvers are still
    // required, so the item still counts as needing approval and the badge has nothing to update.
    if (freshAction.state !== MultiSignActionStateEnum.PENDING) {
      dispatchApprovalRequiredResolved();
    }
    setAuthorisationConflictOutcome(resolveAlreadyActionedOutcome(freshAction, auth?.customer?.id));
    return true;
  };

  // Other approvers still required after this authorisation.
  const [hasApprovedLocally, setHasApprovedLocally] = useState(false);
  // This authorisation completed the action - no more approvals needed.
  const [hasFinalApproval, setHasFinalApproval] = useState(false);
  // Mutually exclusive with hasFinalApproval - this authorisation also completed the action, but
  // the same-day payment run it triggered then failed (individual scheduled payment: caught
  // synchronously via MultiSignErrorCodeEnum.EXECUTION_FAILED; batch: detected via the
  // post-approval group-status fetch, since that run is asynchronous). Drives the paymentFailed/
  // batchFailed outcome instead of the success one - see approve() below.
  const [hasImmediateFailure, setHasImmediateFailure] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [hasRejected, setHasRejected] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [hasDeleted, setHasDeleted] = useState(false);
  // Set instead of opening the confirm dialog (WITHIN_BLOCK_WINDOW - see openDeleteDialog) or
  // instead of the generic deleteError banner (BATCH_DELETE_CONFLICT, batch-only - see
  // confirmDelete/isBatchDeletionBlockedError). Drives UnableToDeleteModal.
  const [unableToDeleteReason, setUnableToDeleteReason] = useState<TUnableToDeleteReason | null>(
    null
  );

  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  // Only ever set for a scheduled_payment/scheduled_transfer retry (no response body to know the
  // new outcome, just a bare 200) - a batch retry instead patches the row in place via
  // onPaymentUpdate, see `retry` below. Drives each modal's own paymentComplete/transferComplete
  // PaymentOutcomeResult branch - a retry always runs synchronously, so success here always means
  // "complete".
  const [hasRetried, setHasRetried] = useState(false);

  const [isMarkAsCompleteDialogOpen, setIsMarkAsCompleteDialogOpen] = useState(false);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markAsCompleteError, setMarkAsCompleteError] = useState<string | null>(null);

  // A different row was opened - don't carry over the previous payment's interaction state.
  useEffect(() => {
    setHasApprovedLocally(false);
    setHasFinalApproval(false);
    setHasImmediateFailure(false);
    setIsApproving(false);
    setApproveError(null);
    setIsRejectDialogOpen(false);
    setIsRejecting(false);
    setRejectError(null);
    setHasRejected(false);
    setIsDeleteDialogOpen(false);
    setIsDeleting(false);
    setDeleteError(null);
    setHasDeleted(false);
    setUnableToDeleteReason(null);
    setIsRetrying(false);
    setRetryError(null);
    setHasRetried(false);
    setIsMarkAsCompleteDialogOpen(false);
    setIsMarkingComplete(false);
    setMarkAsCompleteError(null);
    setAuthorisationConflictOutcome(null);
  }, [payment?.id]);

  const approve = async () => {
    // Belt-and-suspenders alongside the button being hidden for this variant - guards against
    // any stale-render path that could still call this with an authorisation the user never had.
    if (!payment?.multiSignActionId || hasNoApprovalAuthority) {
      setApproveError(scheduledPaymentModal.approveErrorBanner);
      return;
    }

    setIsApproving(true);
    setApproveError(null);

    try {
      // authorise() only ever returns PENDING (more approvers needed) or COMPLETED (this was
      // the last one) - REJECTED/EXPIRED would have failed the backend's pending-state check
      // and landed in the catch block instead, so no other case needs handling here.
      const response = await authoriseMultiSignAction(payment.multiSignActionId);
      const approverCustomerId = auth?.customer?.id;

      // Keeps the cached authorisations list (see useMultiSignApprovers) in sync with what just
      // happened, so reopening this batch/payment later in the same session shows this approval
      // straight away rather than the pre-decision list a hard refresh would otherwise be needed
      // to clear.
      if (approverCustomerId) {
        patchCachedAuthorisation(
          payment.multiSignActionId,
          approverCustomerId,
          MultiSignActionAuthorisationStateEnum.APPROVED
        );
      }

      if (response.data.state === MultiSignActionStateEnum.COMPLETED) {
        // This was the last approval required - the item has definitely left the
        // pending-approval count regardless of what the immediate payment run below does, so the
        // nav badge can just decrement locally instead of paying for a full refetch.
        dispatchApprovalRequiredResolved();

        if (payment.kind === PaymentRowKindEnum.BATCH) {
          // The immediate same-day run happens asynchronously on the backend for a batch, unlike
          // an individual scheduled payment (see the EXECUTION_FAILED catch below) - the
          // authorise call above can't tell us whether it already failed, so fetch the group's
          // own live status instead of optimistically assuming APPROVED.
          try {
            const groupStatus = await getScheduledPaymentGroupStatus(payment.id);
            const nextState = groupStatus.state ?? ScheduledBankPaymentGroupStateEnum.APPROVED;
            onPaymentUpdate?.({
              id: payment.id,
              state: nextState,
              approverCustomerId,
              completedPayments: groupStatus.completedPayments,
              failedPayments: groupStatus.failedPayments
            });
            if (isBatchAllFailed(nextState, groupStatus.completedPayments)) {
              setHasImmediateFailure(true);
            } else {
              // Still IN_PROGRESS (processing not finished yet), or already resolved to a
              // non-failed outcome - either way, useInProgressGroupPolling (see useManagePayment)
              // already watches any IN_PROGRESS row and will pick up the eventual result on its
              // own, same as any other batch left processing in the background.
              setHasFinalApproval(true);
            }
          } catch {
            // Couldn't confirm either way - fall back to the same optimistic behaviour this had
            // before this check existed, rather than leaving the user on neither outcome screen.
            onPaymentUpdate?.({
              id: payment.id,
              state: ScheduledBankPaymentGroupStateEnum.APPROVED,
              approverCustomerId
            });
            setHasFinalApproval(true);
          }
        } else {
          setHasFinalApproval(true);
          onPaymentUpdate?.({
            id: payment.id,
            state: ScheduledBankPaymentGroupStateEnum.APPROVED,
            approverCustomerId
          });
        }
      } else {
        setHasApprovedLocally(true);
        // Group state is unchanged (still PENDING_APPROVAL) while other approvers are pending,
        // so this only updates the "Approved by" attribution - no tab move, and deliberately no
        // dispatchApprovalRequiredResolved() either: the item still counts as needing approval
        // (just from someone else now), so the badge has nothing to decrement yet.
        onPaymentUpdate?.({ id: payment.id, approverCustomerId });
      }
    } catch (error) {
      const body = await unwrapPromiseError<ErrorResponseBody>(error);

      if (isExecutionFailedError(body?.code)) {
        // The final required approval on an individual scheduled payment also runs that day's
        // payment synchronously - this is that run failing, not the approval itself. Safety check
        // first: only trust this as "the action completed and the payment then failed" once a
        // fresh fetch confirms the action really did reach COMPLETED (not still PENDING) - so a
        // stale/unexpected 4104 can't wrongly mark an unrelated payment FAILED and pull it out of
        // Scheduled.
        const freshAction = refetchApprovers ? await refetchApprovers() : null;
        if (freshAction?.state === MultiSignActionStateEnum.COMPLETED) {
          dispatchApprovalRequiredResolved();
          onPaymentUpdate?.({
            id: payment.id,
            state: ScheduledBankPaymentGroupStateEnum.FAILED,
            approverCustomerId: auth?.customer?.id
          });
          setHasImmediateFailure(true);
        } else {
          setApproveError(scheduledPaymentModal.approveErrorBanner);
        }
      } else if (isAuthorisationConflictError(body?.code)) {
        const reconciled = await reconcileAfterAuthorisationConflict(payment);
        // On success, AlreadyActionedModal (driven by authorisationConflictOutcome) explains what
        // happened - no need for the plain-text banner too. Only fall back to it if
        // reconciliation itself wasn't possible (no refetchApprovers, or its own fetch failed).
        if (!reconciled) setApproveError(scheduledPaymentModal.approveErrorBanner);
      } else {
        setApproveError(scheduledPaymentModal.approveErrorBanner);
      }
    } finally {
      setIsApproving(false);
    }
  };

  /**
   * Opens the "Are you sure?" confirmation dialog (see confirmDelete for the actual delete call).
   * The delete button itself is always shown for an APPROVED/FAILED payment (see
   * resolvePaymentActionVariant) - this is where the daily delete-block window is actually
   * checked, at the moment of the click, showing UnableToDeleteModal instead of the confirm
   * dialog when blocked, rather than hiding the button pre-emptively during render.
   *
   * The window check only applies to an upcoming (APPROVED) payment - one that has already
   * failed (RETRY_AND_DELETE) has nothing left to protect against re-sending, since it already
   * ran, so it skips the check entirely.
   *
   * Also re-checks the creator restriction here, not just in each caller's render condition (see
   * ScheduledPaymentModal/BatchOverviewModal's own showDeleteAction) - RETRY_AND_DELETE's delete
   * button was previously rendered for any admin with no creator check at all, so this is the
   * backstop that actually matters, not just belt-and-braces.
   *
   * Also re-checks that the payment isn't still PENDING_APPROVAL - the backstop for the
   * NO_APPROVAL_AUTHORITY leak where a creator with no multi-sign authority could delete a payment
   * straight out of Action Required, bypassing approval entirely. Only APPROVED/FAILED payments
   * (DELETE/RETRY_AND_DELETE) are ever meant to reach this point.
   */
  const openDeleteDialog = () => {
    if (!payment) return;
    if (!isPaymentCreator(payment.createdByCustomerId, auth?.customer?.id)) return;
    if (payment.state === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL) return;

    if (
      payment.state === ScheduledBankPaymentGroupStateEnum.APPROVED &&
      isWithinDeleteBlockWindow(payment.scheduledDate)
    ) {
      setUnableToDeleteReason(UnableToDeleteReasonEnum.WITHIN_BLOCK_WINDOW);
      return;
    }

    setDeleteError(null);
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return; // avoid abandoning an in-flight request
    setIsDeleteDialogOpen(false);
  };

  /**
   * Confirms the delete - `scheduled_payment`/`pending_scheduled_payment` rows call the real
   * per-payment delete endpoint (see resolveScheduledPaymentDeleteId for why the latter needs an
   * extra lookup first); `batch` rows call the group delete endpoint. Either can still 4xx if the
   * row changed server-side after the dialog opened:
   * - Already gone (isDeleteAlreadyGoneError) - reconciled the same as a successful delete, since
   *   the desired end state is already true.
   * - Batch-only, a state/processing-window conflict (isDeletionBlockedError) - swaps in
   *   UnableToDeleteModal instead of the generic banner, since retrying wouldn't help.
   * Any other code (an unrecognised one, or the individual-payment id lookup below failing) falls
   * back to the generic banner.
   */
  const confirmDelete = async () => {
    if (!payment) return;

    setIsDeleting(true);
    setDeleteError(null);
    const strategy = getPaymentActionStrategy(payment.kind);

    try {
      await strategy.deletePayment(payment, {
        accountId: auth?.accountId,
        businessId: auth?.business?.id
      });
      setIsDeleteDialogOpen(false);
      setHasDeleted(true);
      // Removes the row keyed by its CURRENT id in local state (still the multi-sign action's id
      // for a pending_scheduled_payment row, even though the API call above used the real payment
      // id resolved separately) - see removePaymentFromGroups, which matches on this same id.
      onPaymentDelete?.(payment.id);
    } catch (error) {
      const body = await unwrapPromiseError<ErrorResponseBody>(error);

      if (strategy.isDeleteAlreadyGoneError(body?.code)) {
        setIsDeleteDialogOpen(false);
        setHasDeleted(true);
        onPaymentDelete?.(payment.id);
      } else if (strategy.isDeleteConflictError?.(body?.code)) {
        setIsDeleteDialogOpen(false);
        setUnableToDeleteReason(UnableToDeleteReasonEnum.DELETE_CONFLICT);
      } else {
        setDeleteError(scheduledPaymentModal.deleteErrorBanner);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const dismissUnableToDelete = () => setUnableToDeleteReason(null);

  /**
   * Retries a failed (or partially-failed) payment or batch. A batch's retry response includes
   * its new state, so the row is patched in place rather than removed - the caller can see the
   * outcome (still failed, now in progress, etc.) without a refetch. An individual payment's
   * retry returns no body, so there's nothing to patch with from the response itself.
   *
   * A one-off payment/transfer is genuinely done once its retry succeeds, so it's removed, same
   * reasoning as the pending-action terminal-outcome branches in approve/confirmReject above. A
   * RECURRING one isn't done - this run just succeeded, but it'll run again on its next
   * occurrence (isActive stays true server-side, same as the automatic daily retry path - see
   * getActiveScheduledWalletTransfers/getScheduledPayments) - so it's patched back to APPROVED
   * (the Scheduled tab) instead of removed. Without this, a recurring row would vanish from every
   * tab until something else happens to trigger a full refetch.
   */
  const retry = async () => {
    if (!payment) return;

    setIsRetrying(true);
    setRetryError(null);

    try {
      const patch = await getPaymentActionStrategy(payment.kind).retryPayment(payment, {
        accountId: auth?.accountId,
        businessId: auth?.business?.id
      });
      if (patch) {
        onPaymentUpdate?.(patch);
      } else {
        setHasRetried(true);
        const isRecurring =
          !!payment.paymentIntervalType &&
          payment.paymentIntervalType !== ScheduledPaymentIntervalTypeEnum.ONE_OFF;
        if (isRecurring) {
          onPaymentUpdate?.({ id: payment.id, state: ScheduledBankPaymentGroupStateEnum.APPROVED });
        } else {
          onPaymentDelete?.(payment.id);
        }
      }
    } catch (error) {
      const body = await unwrapPromiseError<ErrorResponseBody>(error);

      if (isInsufficientFundsError(body?.code)) {
        // Reuses the same flag approve() sets for EXECUTION_FAILED above - each modal already has
        // its own hasImmediateFailure ? <PaymentOutcomeResult .../> branch (paymentFailed for
        // ScheduledPaymentModal, transferFailed for ScheduledTransferModal), so no caller-side
        // change was needed beyond adding that transferFailed variant.
        setHasImmediateFailure(true);
      } else {
        setRetryError(RETRY_ERROR_BANNER_BY_KIND[payment.kind]);
      }
    } finally {
      setIsRetrying(false);
    }
  };

  const openMarkAsCompleteDialog = () => {
    setMarkAsCompleteError(null);
    setIsMarkAsCompleteDialogOpen(true);
  };
  const closeMarkAsCompleteDialog = () => {
    if (isMarkingComplete) return; // avoid abandoning an in-flight request
    setIsMarkAsCompleteDialogOpen(false);
  };

  /** Batch-only: force-completes a failed/partially-failed batch. Patches state in place. */
  const markAsComplete = async () => {
    if (!payment) return;

    setIsMarkingComplete(true);
    setMarkAsCompleteError(null);

    try {
      const response = await markScheduledPaymentGroupAsComplete(payment.id);
      setIsMarkAsCompleteDialogOpen(false);
      onPaymentUpdate?.({
        id: payment.id,
        state: response.data.state,
        forceCompleted: response.data.forceCompleted
      });
    } catch {
      setMarkAsCompleteError(batchOverviewModal.markAsCompleteErrorBanner);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const openRejectDialog = () => {
    setRejectError(null);
    setIsRejectDialogOpen(true);
  };
  const closeRejectDialog = () => {
    if (isRejecting) return; // avoid abandoning an in-flight request
    setIsRejectDialogOpen(false);
  };

  const confirmReject = async (reason: string) => {
    // Same belt-and-suspenders reasoning as the guard in approve() above.
    if (!payment?.multiSignActionId || hasNoApprovalAuthority) {
      setRejectError(rejectPaymentModal.submitError);
      return;
    }

    setIsRejecting(true);
    setRejectError(null);

    try {
      await rejectMultiSignAction(payment.multiSignActionId, reason);

      // Same reasoning as the patch in approve() above - without this, reopening the batch's
      // Overview later in this session (e.g. from Batch History, per the "rejected by" bug this
      // guards against) would keep reading the pre-rejection cached authorisations list and show
      // nobody as having rejected it, even though payment.rejectionReason itself is already
      // patched in via onPaymentUpdate below.
      const rejectorCustomerId = auth?.customer?.id;
      if (rejectorCustomerId) {
        patchCachedAuthorisation(
          payment.multiSignActionId,
          rejectorCustomerId,
          MultiSignActionAuthorisationStateEnum.REJECTED,
          reason
        );
      }

      setIsRejectDialogOpen(false);
      setHasRejected(true);

      // A rejected action always fully resolves - unlike a non-final approve(), there's no "more
      // approvers still required" state for a rejection, so the nav badge can always decrement
      // locally here instead of paying for a full refetch.
      dispatchApprovalRequiredResolved();

      onPaymentUpdate?.({
        id: payment.id,
        state: ScheduledBankPaymentGroupStateEnum.REJECTED,
        rejectionReason: reason
      });
    } catch (error) {
      const body = await unwrapPromiseError<ErrorResponseBody>(error);

      if (isAuthorisationConflictError(body?.code)) {
        const reconciled = await reconcileAfterAuthorisationConflict(payment);
        if (reconciled) {
          // AlreadyActionedModal takes over from here - close the reason dialog rather than
          // leaving it open with nothing left to confirm.
          setIsRejectDialogOpen(false);
        } else {
          setRejectError(rejectPaymentModal.submitError);
        }
      } else {
        setRejectError(rejectPaymentModal.submitError);
      }
    } finally {
      setIsRejecting(false);
    }
  };

  const resolveVariant = () => {
    if (
      hasApprovedLocally ||
      (payment?.state === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL &&
        hasPersistedApproval)
    ) {
      return PaymentActionVariantEnum.AWAITING_ADDITIONAL_APPROVAL;
    }
    // Same PENDING_APPROVAL gate as hasPersistedApproval just above - hasNoApprovalAuthority is a
    // raw, unscoped multi-sign check (see useApprovalDisplayState), so without it a payment that's
    // moved past PENDING_APPROVAL (e.g. FAILED) but still carries a multiSignActionId from before
    // it was approved would resolve to NO_APPROVAL_AUTHORITY - showing a bare Delete button instead
    // of the correct state-driven variant (Retry+Delete for FAILED) - for any viewer who wasn't a
    // signatory on that original approval.
    if (
      payment?.state === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL &&
      hasNoApprovalAuthority
    ) {
      return PaymentActionVariantEnum.NO_APPROVAL_AUTHORITY;
    }
    return resolvePaymentActionVariant(payment);
  };

  return {
    variant: resolveVariant(),
    // Only true right after approve() itself was called this session (as opposed to
    // hasPersistedApproval, which can be true from the very first render) - callers use this to
    // react specifically to a just-performed non-final approval (e.g. navigating away from a
    // detail view back to a summary screen).
    hasApprovedLocally,
    hasRejected,
    hasFinalApproval,
    hasImmediateFailure,
    hasDeleted,
    hasRetried,
    // Who to credit when the user approves in this session - the group's persisted
    // approverCustomerId is resolved separately by the modal.
    currentUserName: auth?.customer?.name || scheduledPaymentModal.unknownName,
    approve,
    isApproving,
    approveError,
    openDeleteDialog,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    deleteError,
    unableToDeleteReason,
    dismissUnableToDelete,
    retry,
    isRetrying,
    retryError,
    markAsComplete,
    isMarkAsCompleteDialogOpen,
    openMarkAsCompleteDialog,
    closeMarkAsCompleteDialog,
    isMarkingComplete,
    markAsCompleteError,
    isRejectDialogOpen,
    openRejectDialog,
    closeRejectDialog,
    confirmReject,
    isRejecting,
    rejectError,
    authorisationConflictOutcome,
    dismissAuthorisationConflict
  };
};
